import React, { useEffect, useState, useRef } from "react";
import { Box, Card, CardContent, Grid, Typography, CircularProgress, Tooltip } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import {
  PeopleOutlined,
  PersonAddOutlined,
  WorkOutlined,
  DescriptionOutlined,
  TrendingUpOutlined,
  CheckCircleOutlined,
  PendingOutlined,
  AssignmentIndOutlined,
  ArchiveOutlined
} from "@mui/icons-material";
import ApplicationTrafficChart from "../ApplicationTrafficChart";
import { useTranslation } from "react-i18next";

// Professional color palette
const colors = {
  primary: '#0017E7',
  primaryLight: '#3347FF',
  primaryDark: '#0012B8',
  success: '#00C853',
  warning: '#FF9800',
  error: '#F44336',
  info: '#2196F3',
  purple: '#9C27B0',
  text: '#1A1A1A',
  textLight: '#616161',
  textMuted: '#9E9E9E',
  surface: '#FFFFFF',
  surfaceAlt: '#F5F7FA',
  border: '#E0E0E0',
};

const StyledCard = styled(Card)(({ variant = 'default' }) => {
  const variantColors = {
    primary: colors.primary,
    success: colors.success,
    warning: colors.warning,
    info: colors.info,
    purple: colors.purple,
    error: colors.error,
    default: colors.primary,
  };

  const color = variantColors[variant] || variantColors.default;

  return {
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, ${color} 0%, ${color}CC 100%)`,
    },
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 4px 16px ${alpha(color, 0.15)}`,
    }
  };
});

const IconWrapper = styled(Box)(({ variant = 'default' }) => {
  const variantColors = {
    primary: colors.primary,
    success: colors.success,
    warning: colors.warning,
    info: colors.info,
    purple: colors.purple,
    error: colors.error,
    default: colors.primary,
  };

  const color = variantColors[variant] || variantColors.default;

  return {
    width: 48,
    height: 48,
    borderRadius: 10,
    background: alpha(color, 0.1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    '& .MuiSvgIcon-root': {
      fontSize: 24,
      color: color,
    }
  };
});

const StatValue = styled(Typography)({
  fontSize: '2rem',
  fontWeight: 700,
  color: colors.text,
  lineHeight: 1.2,
  marginBottom: 4,
});

const StatTitle = styled(Typography)({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: colors.textLight,
  lineHeight: 1.3,
});

const DashboardTitle = styled(Typography)({
  fontSize: '1.75rem',
  fontWeight: 700,
  color: colors.text,
  marginBottom: 8,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  '&::before': {
    content: '""',
    width: 4,
    height: 32,
    background: `linear-gradient(180deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
    borderRadius: 2,
  }
});

const SectionTitle = styled(Typography)({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: colors.text,
  marginTop: 32,
  marginBottom: 16,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const TooltipContent = styled(Box)({
  padding: '8px 0',
});

const TooltipRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '4px 0',
  gap: 16,
});

const TooltipLabel = styled(Typography)({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: colors.surface,
  textTransform: 'capitalize',
});

const TooltipValue = styled(Typography)({
  fontSize: '0.875rem',
  fontWeight: 700,
  color: colors.surface,
});

function StatCard({ title, value, icon: Icon, variant = 'default', loading = false, breakdown = null }) {
  const { t } = useTranslation();
  const animationFrameRef = useRef(null);
  const animationStartTimeRef = useRef(0);
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const target = Number(value || 0);

    if (loading) {
      cancelAnimationFrame(animationFrameRef.current);
      setDisplayValue(0);
      return;
    }

    const durationMs = 700; // fast ramp-up
    const startValue = displayValue;
    const change = target - startValue;

    if (change === 0) return;

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const step = (timestamp) => {
      if (!animationStartTimeRef.current) {
        animationStartTimeRef.current = timestamp;
      }
      const elapsed = timestamp - animationStartTimeRef.current;
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = easeOutCubic(progress);
      const current = Math.round(startValue + change * eased);
      setDisplayValue(current);
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(step);
      } else {
        animationStartTimeRef.current = 0;
      }
    };

    cancelAnimationFrame(animationFrameRef.current);
    animationStartTimeRef.current = 0;
    animationFrameRef.current = requestAnimationFrame(step);

    return () => cancelAnimationFrame(animationFrameRef.current);
  }, [value, loading]);
  const displayLabels = {
    Attachments: t('superAdminDashboard.cvsFromEmailScraping'),
    cv_pdfs: t('superAdminDashboard.cvsImportedByRecruiters'),
    imported_resumes: t('superAdminDashboard.cvsImportedToPosts'),
    interview: t('superAdminDashboard.cvsUploadedForCaravans'),
    resumes: t('superAdminDashboard.cvsUsedToApplyToJob'),
    spontanious: t('superAdminDashboard.cvsUsedForSpontaneousPost'),
    generated: t('superAdminDashboard.cvsGeneratedBySmartCv'),
    other: t('superAdminDashboard.otherPdfs'),
  };

  const renderTooltip = () => {
    if (!breakdown || Object.keys(breakdown).length === 0) return null;

    return (
      <TooltipContent>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: colors.surface, mb: 1, textAlign: 'center' }}>
          {t('superAdminDashboard.breakdownByDirectory')}
        </Typography>
        {Object.entries(breakdown).map(([key, val]) => (
          <TooltipRow key={key}>
            <TooltipLabel>{displayLabels[key] || key.replace(/_/g, ' ')}</TooltipLabel>
            <TooltipValue>{val.toLocaleString()}</TooltipValue>
          </TooltipRow>
        ))}
      </TooltipContent>
    );
  };

  const cardContent = (
    <StyledCard variant={variant}>
      <CardContent sx={{ p: 3 }}>
        <IconWrapper variant={variant}>
          {Icon && <Icon />}
        </IconWrapper>
        {loading ? (
          <CircularProgress size={32} sx={{ color: colors.primary }} />
        ) : (
          <>
            <StatValue>{Number.isFinite(displayValue) ? displayValue.toLocaleString() : 0}</StatValue>
            <StatTitle>{title}</StatTitle>
          </>
        )}
      </CardContent>
    </StyledCard>
  );

  if (breakdown && Object.keys(breakdown).length > 0) {
    return (
      <Tooltip 
        title={renderTooltip()} 
        arrow 
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: alpha(colors.text, 0.95),
              padding: '12px 16px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              maxWidth: 300,
            }
          },
          arrow: {
            sx: {
              color: alpha(colors.text, 0.95),
            }
          }
        }}
      >
        {cardContent}
      </Tooltip>
    );
  }

  return cardContent;
}

const SuperAdminDashboard = () => {
  const { t, ready } = useTranslation();
  const [myAdminsCount, setMyAdminsCount] = useState(0);
  const [totalAdminsCount, setTotalAdminsCount] = useState(0);

  // Additional statistics for ATC job application system
  const [totalApplications, setTotalApplications] = useState(0);
  const [declinedApplications, setDeclinedApplications] = useState(0);
  const [pendingApplications, setPendingApplications] = useState(0);
  const [approvedApplications, setApprovedApplications] = useState(0);
  const [totalJobs, setTotalJobs] = useState(0);
  const [totalJobsOpen, setTotalJobsOpen] = useState(0);
  const [totalJobsClosed, setTotalJobsClosed] = useState(0);
  const [totalCVs, setTotalCVs] = useState(0);
  const [cvsBreakdown, setCvsBreakdown] = useState({});
  const [activeJobs, setActiveJobs] = useState(0);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('accessToken');

    const fetchStats = async () => {
      try {
        setLoading(true);
        
        // Fetch admin statistics, CV count with detailed breakdown, and approved applications
        const [mineRes, totalRes, cvsRes, approvedRes, pendingRes, refusedRes, totalAppsRes, totalPostsRes] = await Promise.all([
          fetch(`${apiUrl}authentication/superadmin/my-admins-count/`, { 
            headers: { Authorization: `Bearer ${token}` } 
          }),
          fetch(`${apiUrl}authentication/superadmin/total-admins-count/`, { 
            headers: { Authorization: `Bearer ${token}` } 
          }),
          fetch(`${apiUrl}authentication/superadmin/total-cvs-count-detailed/`, { 
            headers: { Authorization: `Bearer ${token}` } 
          }),
          fetch(`${apiUrl}authentication/superadmin/approved-applications-count/`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${apiUrl}authentication/superadmin/pending-applications-count/`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${apiUrl}authentication/superadmin/refused-applications-count/`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${apiUrl}authentication/superadmin/total-applications-count/`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${apiUrl}authentication/superadmin/total-posts-count/`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
        ]);
        
        const mine = await mineRes.json();
        const total = await totalRes.json();
        const cvs = await cvsRes.json();
        const approved = await approvedRes.json();
        const pending = await pendingRes.json();
        const refused = await refusedRes.json();
        const totalApps = await totalAppsRes.json();
        const totalPosts = await totalPostsRes.json();
        setMyAdminsCount(mine.count ?? 0);
        setTotalAdminsCount(total.count ?? 0);
        setTotalCVs(cvs.count ?? 0);
        setCvsBreakdown(cvs.breakdown ?? {});

        // TODO: Fetch application statistics when endpoints are available
        // Uncomment and update these when you have the actual API endpoints:
        
        // const [applicationsRes, jobsRes, cvsRes] = await Promise.all([
        //   fetch(`${apiUrl}applications/stats/`, { headers: { Authorization: `Bearer ${token}` } }),
        //   fetch(`${apiUrl}jobs/stats/`, { headers: { Authorization: `Bearer ${token}` } }),
        //   fetch(`${apiUrl}cvs/stats/`, { headers: { Authorization: `Bearer ${token}` } }),
        // ]);
        
        // const applicationsData = await applicationsRes.json();
        // const jobsData = await jobsRes.json();
        // const cvsData = await cvsRes.json();
        
        // setTotalApplications(applicationsData.total ?? 0);
        // setPendingApplications(applicationsData.pending ?? 0);
        // setApprovedApplications(applicationsData.approved ?? 0);
        // setTotalJobs(jobsData.total ?? 0);
        // setActiveJobs(jobsData.active ?? 0);
        // setTotalCVs(cvsData.total ?? 0);
        
        // Use live approved applications count from API
        setApprovedApplications(approved?.total_hired_count ?? 0);
        setPendingApplications(pending?.total_pending_count ?? 0);
        // Keep global total for heading badge
        setTotalApplications(totalApps?.total_applications ?? 0);
        // Show declined in the Total Applications card
        setDeclinedApplications(refused?.total_refused_count ?? 0);
        setTotalJobs(totalPosts?.total_posts ?? 0);
        setTotalJobsOpen(totalPosts?.total_open_posts ?? 0);
        setTotalJobsClosed(totalPosts?.total_closed_posts ?? 0);
        setActiveJobs(totalPosts?.total_open_posts ?? 0);
        // setTotalCVs(2341);
        
      } catch (e) {
        console.error('Failed to load SuperAdmin stats', e);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (!ready) {
    return (
      <Box p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <DashboardTitle>
        {t('superAdminDashboard.title')}
      </DashboardTitle>
      <Typography sx={{
        fontSize: '0.95rem',
        color: colors.textLight,
        mb: 3
      }}>
        {t('superAdminDashboard.subtitle')}
      </Typography>

      {/* Admin Management + Job Listings (side-by-side) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'flex-start' },
          gap: 2,
          mb: 2,
        }}
      >
        {/* Left: Admin Management */}
        <Box sx={{ flex: 1 }}>
          <SectionTitle>
            <PeopleOutlined sx={{ color: colors.primary }} />
            {t('superAdminDashboard.adminManagement')}
          </SectionTitle>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={6}>
              <StatCard
                title={t('superAdminDashboard.adminsCreatedByMe')}
                value={myAdminsCount}
                icon={PersonAddOutlined}
                variant="primary"
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <StatCard
                title={t('superAdminDashboard.totalAdminsInSystem')}
                value={totalAdminsCount}
                icon={PeopleOutlined}
                variant="info"
                loading={loading}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Divider (visible on md and up) */}
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            width: '1px',
            alignSelf: 'stretch',
            backgroundColor: colors.border,
          }}
        />

        {/* Right: Job Listings */}
        <Box sx={{ flex: 1 }}>
          <SectionTitle>
            <TrendingUpOutlined sx={{ color: colors.info }} />
            {t('superAdminDashboard.jobListings')}
            <Typography component="span" sx={{ ml: 1, color: colors.textLight, fontWeight: 600 }}>
              ({totalJobs?.toLocaleString?.() ?? 0} {t('superAdminDashboard.total')})
            </Typography>
          </SectionTitle>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={6}>
              <StatCard
                title={t('superAdminDashboard.closedJobPostings')}
                value={totalJobsClosed}
                icon={ArchiveOutlined}
                variant="error"
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <StatCard
                title={t('superAdminDashboard.openJobPostings')}
                value={totalJobsOpen}
                icon={WorkOutlined}
                variant="success"
                loading={loading}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Job Applications Section */}
      <SectionTitle>
        <WorkOutlined sx={{ color: colors.success }} />
        {t('superAdminDashboard.jobApplicationsOverview')}
        <Typography component="span" sx={{ ml: 1, color: colors.textLight, fontWeight: 600 }}>
          ({totalApplications?.toLocaleString?.() ?? 0} {t('superAdminDashboard.totalApplications')})
        </Typography>
      </SectionTitle>
      <Grid container spacing={2.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('superAdminDashboard.applicationDeclined')}
            value={declinedApplications}
            icon={AssignmentIndOutlined}
            variant="error"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('superAdminDashboard.pendingReview')}
            value={pendingApplications}
            icon={PendingOutlined}
            variant="warning"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('superAdminDashboard.approvedApplications')}
            value={approvedApplications}
            icon={CheckCircleOutlined}
            variant="success"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('superAdminDashboard.totalCvsUploaded')}
            value={totalCVs}
            icon={DescriptionOutlined}
            variant="purple"
            loading={loading}
            breakdown={cvsBreakdown}
          />
        </Grid>
      </Grid>

      {/* Application Traffic Over Time */}
      <SectionTitle>
        <TrendingUpOutlined sx={{ color: colors.primary }} />
        {t('superAdminDashboard.applicationTrafficOverTime')}
      </SectionTitle>
      <Box sx={{ mt: 1 }}>
        <ApplicationTrafficChart />
      </Box>
    </Box>
  );
};

export default SuperAdminDashboard;